import { z } from 'zod';
import { PLAN_FEATURES } from '../constants/plans.js';
import { ACTIVITY_CATEGORIES, SUPPORT_ACCESS_STATUSES } from '../constants/activity.js';
import { MERCHANT_STATUSES } from '../types/enums.js';
import { dateSchema, moneySchema, optionalMoneySchema, paginationQuerySchema, reportPeriodSchema, uuidSchema, } from './common.js';
/**
 * Super Admin (platform) requests.
 *
 * Kept apart from the merchant schemas on purpose: nothing in here may ever be
 * reachable from a merchant's own dashboard.
 */
/** Switch a platform user's copy of merchants' paid-order emails on or off. */
export const setPlatformUserCcSchema = z.object({
    ccOrderEmails: z.boolean(),
});
/**
 * Suspend a store, or lift its suspension.
 *
 * Suspension is per STORE, not per person: while a store is suspended nobody
 * under it can sign in or use their session, and its storefront is offline.
 * Suspending needs a reason — it takes a whole business offline, and the record
 * should say why.
 */
export const setStoreStatusSchema = z
    .object({
    status: z.enum(['ACTIVE', 'SUSPENDED']),
    reason: z.string().trim().max(500).optional(),
})
    .refine((value) => value.status !== 'SUSPENDED' || (value.reason?.length ?? 0) >= 3, {
    message: 'Say why this store is being suspended',
    path: ['reason'],
});
/** The stores list: filter by status, search by store, slug or owner email. */
export const adminStoresQuerySchema = paginationQuerySchema.extend({
    status: z.enum(MERCHANT_STATUSES).optional(),
    search: z.string().trim().max(200).optional(),
});
/** Platform-wide sales, over the same named periods the merchant dashboard uses. */
export const adminOverviewQuerySchema = z
    .object({
    period: reportPeriodSchema.default('this_month'),
    from: dateSchema.optional(),
    to: dateSchema.optional(),
})
    .refine((value) => value.period !== 'custom' || (value.from && value.to), {
    message: 'A custom range needs both a start and an end date',
    path: ['from'],
});
/**
 * Open a merchant's account for troubleshooting. A reason is required: the
 * access is recorded, and a record that says only "looked" is not worth much
 * to the merchant it was about.
 */
export const startSupportAccessSchema = z.object({
    reason: z.string().trim().min(3, 'Say what you are troubleshooting').max(500),
});
/** The curated activity log: pick a category, optionally narrow to one store. */
export const adminActivityQuerySchema = paginationQuerySchema.extend({
    category: z.enum(ACTIVITY_CATEGORIES).default('all'),
    storeId: uuidSchema.optional(),
});
/** Every time an operator opened a store: filter by where it stands, or by store. */
export const adminAccessLogQuerySchema = paginationQuerySchema.extend({
    status: z.enum(SUPPORT_ACCESS_STATUSES).optional(),
    storeId: uuidSchema.optional(),
});
/** A limit is a whole number of 0 or more, or null for unlimited. */
const planLimitSchema = z.number().int().min(0).max(1_000_000).nullable();
/** Edit a plan's wording, prices, limits and features. Only what is sent changes. */
export const updatePlanSchema = z.object({
    name: z.string().trim().min(2).max(60).optional(),
    description: z.string().trim().max(300).nullable().optional(),
    priceMonthly: moneySchema.optional(),
    priceYearly: optionalMoneySchema,
    limits: z
        .object({
        products: planLimitSchema.optional(),
        salesPages: planLimitSchema.optional(),
    })
        .optional(),
    features: z.array(z.enum(PLAN_FEATURES)).optional(),
    isActive: z.boolean().optional(),
});
/**
 * Put a store on a plan by hand, valid for a number of days from now — for
 * comps, offline payments and fixing a mistake. Paid-by-gateway upgrades go
 * through billing instead.
 */
export const assignPlanSchema = z.object({
    planId: uuidSchema,
    days: z.number().int().min(1).max(3650),
});
/** The platform's Billplz account for charging merchants. Blank secrets keep what is saved. */
export const savePlatformBillingSchema = z.object({
    enabled: z.boolean(),
    mode: z.enum(['sandbox', 'live']),
    collectionId: z.string().trim().max(120),
    apiKey: z.string().trim().max(200).optional(),
    xSignatureKey: z.string().trim().max(200).optional(),
});
//# sourceMappingURL=admin.js.map